import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { ArrowDown, ArrowUp, ArrowUpDown, MonitorSmartphone, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Choice, type ChoiceItem } from '@/components/Choice'
import { Err } from '@/components/Err'
import { Pager } from '@/components/Pager'
import { Empty, PageHead, SectionCrumb } from '@/components/layout/Page'
import { UNREACHABLE_WORD, isLive, leaseWord, vmActions, vmWord, type VmAction } from '@/domain/lease'
import { leases as leaseApi, vmMetrics as vmMetricsApi, type LeaseQuery } from '@/api/endpoints'
import type { LeaseSort, LeaseState, VmMetrics } from '@/api/types'
import { PAGE_SIZES, leaseChanged, usePage } from '@/hooks/usePage'
import { usePoll } from '@/hooks/usePoll'
import { useWorldRefresh } from '@/hooks/useWorld'
import { cn } from '@/lib/utils'
import { useSession } from '@/hooks/useSession'
import { ariaSort, nextSort, type Sort } from '@/ui/sort'
import { VmBatch } from './vm/batch'
import type { View } from './vm/listRow'
import { VmRow } from './vm/VmRow'
import { PendingChanges } from './vm/PendingChanges'
import { pill } from './pills'
import { useArrivedNotice } from './vm/notice'
import type { LeaseRow, World } from './model'

// 서버(VM)는 사람이 받아서 쓰는 단위다. 한 줄이 임대 하나 — 사용자 · VM · CPU · RAM · DISK · 네트워크 · 디스크 · GPU · 상태.
// GPU 칸은 받은 카드의 모델과 장 수뿐이다 — 카드의 사용량은 재지 않는다. 카드가 아직 없거나 반납된 줄은 신청한 모델·장 수다.
// 자원 칸은 사용자의 내 VM 목록과 같은 고리·줄이다(F-48): 사양은 고리의 총량이 말하므로 사양 열을 따로 두지 않는다.
// DISK 칸은 고리 없는 할당 글자다 — 루트와 데이터 볼륨의 합이고 쿼터가 세는 것과 같다. 게스트가 쓴 양은 보이지 않는다.
// 만들기는 VM 생성 페이지(`vm/CreateVm.tsx`)에서 한다 — 사용자의 내 VM 과 같은 페이지이고, 끝나면 결과 한 줄을 들고 이 목록으로 돌아온다.
// 관리자에게는 검토 대기가 없으므로 자기 팀의 변경 신청이 이 화면 위에 보기로만 선다 — 결정은 최고 관리자의 것이다.
//
// 목록은 서버의 쪽이다(`hooks/usePage.ts`) — 거르고 찾고 세우는 것은 서버가 하고 화면은 받은 쪽을 받은 순서대로 그린다.
// 머리의 `VM n대 · 임대 n건` 은 거르개·찾기와 상관없이 보는 사람에게 살아 있는 수(`counts`)다.
// 표 바로 위가 도구 한 줄이다 — 검색 칸, 그 바로 오른쪽의 거르개 알약 셋과 상태 거르개, 그 뒤의 일괄 조작 칩(`vm/batch.tsx`).
// 알약은 하나만 켜진다: `전체` 는 살아 있는 VM, `보존 중` 은 보존 기한 안의 반납 VM, `반납됨` 은 반납이 끝난 VM 이다.
// 반려·실패한 신청은 VM 이 된 적이 없어 어느 알약에도 없다 — 사용자 상세의 지난 임대와 검토 대기가 말한다.
// 상태 거르개는 `전체` 에만 서고 상태 칸의 배지가 말하는 낱말 하나를 고른다. 상태는 찾는 말이 아니다 — 검색 칸은 낱말로 상태를 찾지 않는다.
// 검색 칸과 상단 바의 찾기는 서버의 찾는 말 둘이고 둘 다 걸린다.
// 체크박스는 보는 사람이 조작할 수 있는 줄에만 서고(관리자에게는 자기 VM 뿐이다), 머리의 전체 선택과 일괄 조작은 **지금 보이는 쪽의 줄만** 대상이다.
// 쪽·알약·상태·검색·정렬이 바뀌면 고르기를 비운다 — 보이지 않는 VM 에 조작하지 않는다.
// 머리를 누르면 서버가 그 열로 세우고 다시 누르면 뒤집고 한 번 더 누르면 푼다 — 누르기 전과 푼 뒤는 신청이 새것부터다.
// 흐르는 값(사용량·네트워크·디스크 입출력)으로는 세우지 않는다 — 줄이 읽을 때마다 자리를 바꾼다.
// `보존 중` 에서는 네트워크·디스크 입출력 칸 대신 `종료`(반납한 때)와 `반납 예정`(보존 기한, 아래 줄에 남은 때)이 선다 —
// 꺼진 VM 이라 입출력은 늘 비고, 보존 중 VM 에 대해 정할 것은 언제 지워지는가뿐이다. 이 둘은 좁은 화면에서도 남는다.
// 한 줄은 `vm/VmRow.tsx` 의 `memo` 줄이라 지금 읽기나 고르기가 바뀐 줄만 다시 그린다. 줄이 그리는 이름·배지·총량은
// `vm/listRow.ts` 의 함수다(디스크 할당은 `domain/lease.ts` 의 `diskAllocatedGb`).

// 지금 읽기는 값이라 변경 알림으로 대신할 수 없다 — 정해진 주기로 다시 묻고, 탭이 숨으면 `usePoll` 이 멈춘다.
// 백엔드는 PVE 를 묻지 않고 건강 스윕의 기억에서 답하므로 더 자주 물어도 새 값이 없다.
// 스윕 기본 주기(`clusterhealth.DefaultInterval`)에 맞춘다.
const METRICS_POLL_MS = 5000
// 목록은 변경 알림에 다시 읽고(최고 관리자), 알림이 없는 사이는 이 간격이 메운다 — `World` 의 목록과 같은 간격이다.
const LEASES_POLL_MS = 8000

// 거르개 알약. 하나만 켜지고 `전체` 가 기본이다.
const VIEWS: { view: View; word: string }[] = [
  { view: 'live', word: '전체' },
  { view: 'retired', word: '보존 중' },
  { view: 'ended', word: '반납됨' },
]
// 알약이 고르는 임대의 상태. `전체` 는 살아 있는 넷이다.
const LIVE: LeaseState[] = ['requested', 'pending_approval', 'active', 'ending']
const VIEW_STATES: Record<View, LeaseState[]> = { live: LIVE, retired: ['retired'], ended: ['ended'] }

type Scope = Pick<LeaseQuery, 'state' | 'vm_state' | 'unreachable'>

// 상태 거르개의 항목 — 상태 칸의 배지가 `전체` 에서 말할 수 있는 낱말 그대로다(`vm/listRow.ts` 의 `badges`).
// 임대의 낱말은 그 임대 상태로, VM 의 낱말은 산 게스트의 상태로, `닿지 않음` 은 닿지 못하는 게스트로 거른다.
// 실행 중인 VM 은 배지를 따로 세우지 않으므로(`사용 중` 하나) 그 낱말도 여기 없다.
const STATE_FILTERS: { value: string; word: string; scope: Scope }[] = [
  ...(['requested', 'pending_approval', 'active', 'ending'] as const).map((s) => ({
    value: `lease:${s}`,
    word: leaseWord(s),
    scope: { state: [s] },
  })),
  ...(['planned', 'provisioning', 'stopped', 'destroying'] as const).map((s) => ({
    value: `vm:${s}`,
    word: vmWord(s),
    scope: { state: LIVE, vm_state: [s] },
  })),
  { value: 'unreachable', word: UNREACHABLE_WORD, scope: { state: LIVE, unreachable: true } },
]
// 거르지 않는 항목. `전체` 는 알약의 낱말이라 다른 낱말을 쓴다 — 한 낱말은 한 뜻만.
const STATE_ITEMS: ChoiceItem[] = [{ value: '', title: '모든 상태' }, ...STATE_FILTERS.map((f) => ({ value: f.value, title: f.word }))]

function scopeOf(view: View, state: string): Scope {
  if (view !== 'live') return { state: VIEW_STATES[view] }
  return STATE_FILTERS.find((f) => f.value === state)?.scope ?? { state: LIVE }
}

type Col = LeaseSort

function SortHead({
  col,
  sort,
  onSort,
  className,
  children,
}: {
  col: Col
  sort: Sort<Col> | null
  onSort: (col: Col) => void
  className: string
  children: ReactNode
}) {
  const dir = sort?.key === col ? sort.dir : null
  const Icon = dir === 'asc' ? ArrowUp : dir === 'desc' ? ArrowDown : ArrowUpDown
  return (
    <TableHead className={className} aria-sort={ariaSort(sort, col)}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className="inline-flex items-center gap-1 hover:text-brand-hover"
      >
        {children}
        <Icon aria-hidden className={cn('size-3', dir ? 'text-foreground' : 'text-muted-foreground')} />
      </button>
    </TableHead>
  )
}

export default function VmList({ world, query }: { world: World; query: string }) {
  const nav = useNavigate()
  const me = useSession()
  const refresh = useWorldRefresh()
  const [view, setView] = useState<View>('live')
  const [state, setState] = useState('')
  const [find, setFind] = useState('')
  const [notice, setNotice] = useArrivedNotice()
  const [sort, setSort] = useState<Sort<Col> | null>(null)
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set())
  const [busy, setBusy] = useState<VmAction | null>(null)
  const filters: LeaseQuery = { ...scopeOf(view, state), ...(sort ? { sort: sort.key, dir: sort.dir } : {}) }
  const page = usePage({
    read: ({ terms, limit, offset }) => leaseApi.page({ ...filters, q: terms, limit, offset }),
    // 검색 칸이 먼저, 상단 바의 찾기가 둘째다 — 빈 말은 싣지 않는다.
    terms: [find, query],
    filters,
    match: leaseChanged,
    pollMs: LEASES_POLL_MS,
  })
  const items = page.data?.items
  const rows: LeaseRow[] = (items ?? []).map((l) => ({ lease: l, vm: l.vm && isLive(l.vm) ? l.vm : null }))
  const counts = page.data?.counts

  // 지금 읽기는 이 쪽의 활성 줄만 묻는다. 활성 줄이 없으면 묻지 않는다 — lease_id 가 없는 읽기는 모든 VM 이다.
  const active = useMemo(() => (items ?? []).filter((l) => l.state === 'active').map((l) => l.id), [items])
  const metrics = usePoll(
    () => (active.length > 0 ? vmMetricsApi.listFor(active) : Promise.resolve<VmMetrics[]>([])),
    METRICS_POLL_MS,
    [active.join(',')],
  )
  const byLease = new Map<string, VmMetrics>((metrics.data ?? []).map((m) => [m.lease_id, m]))
  // 줄은 노드의 이름(원시값)만 받는다 — 줄마다 노드 목록을 뒤지지 않는다.
  const nodeNames = useMemo(() => new Map(world.nodes.map((n) => [n.node.id, n.name])), [world.nodes])
  const onSort = (col: Col) => setSort((cur) => nextSort(cur, col))

  // 고르기는 지금 쪽의 것이다 — 쪽·알약·상태·검색·정렬이 바뀌면 비운다. 렌더 중에 비우므로 옛 고르기가 새 쪽에 한 번도 서지 않는다.
  const [pickedAt, setPickedAt] = useState(page.at)
  if (pickedAt !== page.at) {
    setPickedAt(page.at)
    setPicked(new Set())
  }
  // 고를 수 있는 줄은 보는 사람이 조작할 수 있는 줄이다.
  const pickable = (r: LeaseRow) => vmActions(r, me).length > 0
  const visible = rows.filter(pickable).map((r) => r.lease.id)
  const chosen = rows.filter((r) => picked.has(r.lease.id) && pickable(r))
  const allPicked = visible.length > 0 && visible.every((id) => picked.has(id))
  const locked = busy !== null
  // 줄에 넘기는 함수는 고정이다 — 그릴 때마다 새 함수를 주면 `memo` 줄이 모두 다시 그려진다(`vm/VmRow.tsx`).
  const pick = useCallback(
    (ids: string[], on: boolean) =>
      setPicked((prev) => {
        const next = new Set(prev)
        for (const id of ids) {
          if (on) next.add(id)
          else next.delete(id)
        }
        return next
      }),
    [],
  )
  const onPick = useCallback((id: string, on: boolean) => pick([id], on), [pick])
  const onOpen = useCallback((leaseId: string) => nav(`/vms/${leaseId}`), [nav])

  return (
    <>
      <SectionCrumb />
      {me.role === 'admin' ? <PendingChanges /> : null}
      <PageHead
        icon={<MonitorSmartphone className="size-5" />}
        title="VM"
        facts={counts ? `VM ${counts.vms}대 · 임대 ${counts.leases}건${chosen.length > 0 ? ` · ${chosen.length}대 선택` : ''}` : undefined}
        right={
          <div className="mt-0.5 flex items-center gap-2">
            <Button asChild className="bg-brand hover:bg-brand-hover">
              <Link to="/vms/new">
                <Plus className="size-3.5" />
                VM 생성
              </Link>
            </Button>
          </div>
        }
      />

      {notice ? (
        <div
          role="status"
          className={cn(
            'mt-4 flex items-start gap-2 rounded-lg border px-3 py-2 text-[12px]',
            notice.tone === 'ok' ? 'border-ok/35 bg-ok/8 text-ok-ink' : 'border-danger/35 bg-danger/8 text-danger-ink',
          )}
        >
          <span className="flex-1">{notice.text}</span>
          <button onClick={() => setNotice(null)} className="text-muted-foreground hover:text-foreground">
            닫기
          </button>
        </div>
      ) : null}

      <section className="pt-6">
        {/* 검색 칸 바로 오른쪽이 거르개 알약과 상태 거르개이고 그 뒤가 조작이다 — 줄 끝에 붙이지 않는다. 좁은 화면에서는 검색 칸이 줄을 다 쓰고
            알약과 칩이 아랫줄로 접힌다. 조작의 첫 오류는 이 줄의 아랫줄이다(`vm/batch.tsx`). */}
        <div className="mb-2.5 flex flex-wrap items-center gap-2">
          <div className="relative w-64 max-sm:w-full">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-muted-foreground" />
            {/* 아이콘과 입력뿐이다 — 검색 아이콘이 있는 칸이 무엇인지는 아이콘이 말한다. */}
            <Input
              value={find}
              onChange={(e) => setFind(e.target.value)}
              aria-label="검색"
              maxLength={100}
              disabled={locked}
              className="h-8 rounded-full bg-background pl-9 text-base sm:text-[13px]"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {VIEWS.map((v) => (
              <button
                key={v.view}
                type="button"
                disabled={locked}
                onClick={() => {
                  setView(v.view)
                  setState('')
                  // 보존 중에만 있는 칸으로 세운 정렬은 그 칸과 함께 풀린다.
                  if (v.view !== 'retired' && (sort?.key === 'retired' || sort?.key === 'purge')) setSort(null)
                }}
                aria-pressed={view === v.view}
                className={pill(view === v.view)}
              >
                {v.word}
              </button>
            ))}
          </div>
          {view === 'live' ? (
            <div className="w-32">
              <Choice id="vm-state" compact label="상태" value={state} items={STATE_ITEMS} disabled={locked} onChange={setState} />
            </div>
          ) : null}
          <VmBatch
            chosen={chosen}
            busy={busy}
            onBusy={setBusy}
            onSettled={(done) => {
              pick([...done], false)
              refresh()
            }}
          />
        </div>

        <Err error={page.error} />
        {rows.length === 0 ? (
          page.error && !page.data ? null : (
            <Empty>
              <MonitorSmartphone className="size-4" /> {page.loading ? '불러오는 중' : query || find ? '검색 결과 없음' : 'VM 없음'}
            </Empty>
          )
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-10">
                      {visible.length > 0 ? (
                        <Checkbox
                          aria-label="VM 전체 선택"
                          checked={allPicked}
                          disabled={locked}
                          onCheckedChange={(on) => pick(visible, on === true)}
                        />
                      ) : null}
                    </TableHead>
                    <SortHead col="owner" sort={sort} onSort={onSort} className="w-32">사용자</SortHead>
                    <SortHead col="name" sort={sort} onSort={onSort} className="w-44">VM</SortHead>
                    <SortHead col="cores" sort={sort} onSort={onSort} className="w-28 text-center max-sm:hidden">CPU</SortHead>
                    <SortHead col="memory" sort={sort} onSort={onSort} className="w-28 text-center max-sm:hidden">메모리</SortHead>
                    <SortHead col="disk" sort={sort} onSort={onSort} className="w-28 text-center max-sm:hidden">디스크</SortHead>
                    {view === 'retired' ? (
                      <>
                        <SortHead col="retired" sort={sort} onSort={onSort} className="w-36">종료</SortHead>
                        <SortHead col="purge" sort={sort} onSort={onSort} className="w-36">반납 예정</SortHead>
                      </>
                    ) : (
                      <>
                        <TableHead className="w-28 max-sm:hidden">네트워크</TableHead>
                        <TableHead className="w-28 max-sm:hidden">디스크</TableHead>
                      </>
                    )}
                    <TableHead className="w-28 max-sm:hidden">GPU</TableHead>
                    <SortHead col="state" sort={sort} onSort={onSort} className="w-44">상태</SortHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <VmRow
                      key={r.lease.id}
                      lease={r.lease}
                      vm={r.vm}
                      m={r.lease.state === 'active' ? byLease.get(r.lease.id) : undefined}
                      nodeName={r.vm?.node_id ? nodeNames.get(r.vm.node_id) : undefined}
                      view={view}
                      can={pickable(r)}
                      picked={picked.has(r.lease.id)}
                      locked={locked}
                      onOpen={onOpen}
                      onPick={onPick}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* 쪽과 쪽의 크기. 일괄 조작이 도는 동안은 쪽을 옮기지 않는다 — 고른 줄은 지금 쪽의 것이다. */}
            <div className="flex flex-wrap items-center gap-2 pt-3">
              <Pager page={page.page} pages={page.pages} onPage={page.setPage} disabled={locked} className="mx-0 w-auto flex-1" />
              <div className="flex items-center gap-1.5">
                {PAGE_SIZES.map((n) => (
                  <button
                    key={n}
                    type="button"
                    disabled={locked}
                    onClick={() => page.setSize(n)}
                    aria-pressed={page.size === n}
                    className={pill(page.size === n)}
                  >
                    {n}건
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </section>
    </>
  )
}
